import { useState, type ClipboardEvent } from 'react';
import { format } from 'date-fns';
import { ImagePlus, Loader2, Trash2 } from 'lucide-react';
import { toast } from 'sonner@2.0.3';

import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { fetchMediaStatsBatched } from '../api/statistics';
import { enrichReportRows } from '../lib/enrich-report-rows';
import { buildAccountReportDocx, generateAccountReportDocx } from '../lib/export-account-report-docx';
import { generateAccountReportXlsx } from '../lib/export-account-report-xlsx';
import { openDocxInGoogleDocs, requestGoogleDocsToken } from '../lib/open-docx-in-gdocs';
import { ExportMenu, FORMAT_DOCX, FORMAT_GDOC, FORMAT_XLSX } from './ExportMenu';
import { GoogleDocsLinks } from './GoogleDocsLinks';
import {
  buildLinkRows,
  classifyLink,
  parseLinks,
  rowsMissingViews,
  toReportRows,
  youtubeVideoId,
  type LinkRow,
} from '../lib/minister-links';

import type { MediaStats, MediaStatsError } from '../types/statistics';

const KIND_LABEL: Record<LinkRow['kind'], string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
  other: 'Сайт',
};

// Скрин от человека пересжимаем в JPEG не шире 1080 px: Word и Excel берут
// его одинаково, а файл отчёта не раздувается от PNG со скриншота телефона.
async function imageToJpegUrl(file: Blob): Promise<string> {
  const bitmap = await createImageBitmap(file);
  const scale = Math.min(1, 1080 / bitmap.width);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Не удалось прочитать картинку'))), 'image/jpeg', 0.9),
  );
  return URL.createObjectURL(blob);
}

// YouTube: название и превью без ключа API — oEmbed и статичное превью ролика.
async function youtubeDetails(url: string): Promise<{ theme: string; imageUrl: string }> {
  const id = youtubeVideoId(url);
  const imageUrl = id ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg` : '';
  try {
    const res = await fetch(`https://www.youtube.com/oembed?format=json&url=${encodeURIComponent(url)}`);
    if (!res.ok) return { theme: '', imageUrl };
    const data = (await res.json()) as { title?: string };
    return { theme: data.title ?? '', imageUrl };
  } catch {
    return { theme: '', imageUrl };
  }
}

export function MinisterLinksReport(): JSX.Element {
  const [text, setText] = useState('');
  const [title, setTitle] = useState(() => `Отчёт министру ${format(new Date(), 'MM.yyyy')}`);
  const [rows, setRows] = useState<LinkRow[]>([]);
  const [progress, setProgress] = useState('');
  const [isCollecting, setIsCollecting] = useState(false);
  const [exporting, setExporting] = useState<'docx' | 'xlsx' | 'gdoc' | null>(null);
  const isExporting = exporting !== null;
  const [docUrls, setDocUrls] = useState<string[]>([]);

  const updateRow = (index: number, patch: Partial<LinkRow>) =>
    setRows((prev) => prev.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  const removeRow = (index: number) => setRows((prev) => prev.filter((_, i) => i !== index));

  const attachImage = async (index: number, file: Blob | null | undefined) => {
    if (!file || !file.type.startsWith('image/')) return;
    try {
      updateRow(index, { imageUrl: await imageToJpegUrl(file) });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось прочитать картинку');
    }
  };

  const onPasteImage = (index: number) => (e: ClipboardEvent) => {
    const item = Array.from(e.clipboardData.items).find((i) => i.type.startsWith('image/'));
    if (!item) return;
    e.preventDefault();
    void attachImage(index, item.getAsFile());
  };

  const handleCollect = async () => {
    const urls = parseLinks(text);
    if (urls.length === 0) {
      toast.error('Вставьте хотя бы одну ссылку');
      return;
    }
    setIsCollecting(true);
    setRows([]);
    try {
      const ours = urls.filter((u) => {
        const kind = classifyLink(u);
        return kind === 'instagram' || kind === 'tiktok';
      });
      const results: MediaStats[] = [];
      const errors: MediaStatsError[] = [];
      if (ours.length > 0) {
        setProgress(`Статистика по ссылкам Instagram и TikTok: 0 / ${ours.length}`);
        await fetchMediaStatsBatched(
          { urls: ours },
          {
            onProgress: (p) => setProgress(`Статистика по ссылкам Instagram и TikTok: ${p.completedUrls} / ${p.totalUrls}`),
            onBatchResults: (r, e) => {
              results.push(...r);
              errors.push(...e);
            },
            onPause: (reason) => setProgress(reason),
            onResume: () => {},
          },
        );
      }

      let built = buildLinkRows(urls, results, errors);

      // Тема и перезалитое превью — тем же шагом, что в отчёте по аккаунту
      // (общий кэш команды, OCR по кадру).
      const auto = built.filter((r) => r.source === 'auto' && r.mediaId);
      if (auto.length > 0) {
        setProgress('Темы и превью публикаций…');
        const enriched = await enrichReportRows(
          auto.map((r) => ({ mediaId: r.mediaId!, thumbnailUrl: r.thumbnailUrl ?? undefined, caption: r.caption ?? undefined })),
          { onProgress: (done, total) => setProgress(`Темы и превью публикаций: ${done} / ${total}`) },
        );
        built = built.map((r) => {
          const e = r.mediaId ? enriched.get(r.mediaId) : undefined;
          return e ? { ...r, theme: e.theme, imageUrl: e.imageUrl || r.thumbnailUrl || '' } : r;
        });
      }

      const yt = built.map((r, i) => [r, i] as const).filter(([r]) => r.kind === 'youtube');
      if (yt.length > 0) {
        setProgress('Названия роликов YouTube…');
        const details = await Promise.all(yt.map(([r]) => youtubeDetails(r.url)));
        built = built.map((r) => {
          const k = yt.findIndex(([y]) => y === r);
          return k >= 0 ? { ...r, theme: r.theme || details[k].theme, imageUrl: r.imageUrl || details[k].imageUrl } : r;
        });
      }

      setRows(built);
      const manual = built.filter((r) => r.source === 'manual').length;
      toast.success(
        manual > 0
          ? `Готово: ${built.length - manual} ссылок заполнено, ${manual} ждут просмотров и скрина`
          : `Готово: ${built.length} ссылок`,
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось собрать статистику');
    } finally {
      setIsCollecting(false);
      setProgress('');
    }
  };

  const missing = rowsMissingViews(rows);

  const handleExport = async (kind: string) => {
    if (missing.length > 0) {
      toast.error(`Заполните просмотры у ${missing.length} ссылок или удалите их`);
      return;
    }
    setExporting(kind as 'docx' | 'xlsx' | 'gdoc');
    setDocUrls([]);
    try {
      // Вход в Google — первым делом, пока клик ещё свежий.
      const token = kind === 'gdoc' ? await requestGoogleDocsToken() : null;
      const params = { accountTitle: title.trim() || 'Отчёт министру', period: '', rows: toReportRows(rows) };
      if (token) setDocUrls(await openDocxInGoogleDocs(token, [await buildAccountReportDocx(params)]));
      else if (kind === 'docx') await generateAccountReportDocx(params);
      else await generateAccountReportXlsx(params);
      toast.success('Отчёт сформирован');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Не удалось сформировать отчёт');
    } finally {
      setExporting(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Сводка по списку ссылок</CardTitle>
          <CardDescription>
            Вставьте ссылки на публикации — свои паблики Instagram и TikTok заполнятся сами. Для партнёров,
            YouTube и сайтов впишите просмотры и приложите скрин.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="minister-title">Заголовок в шапке документа</Label>
            <Input id="minister-title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="minister-links">Ссылки</Label>
            <Textarea
              id="minister-links"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={'https://www.instagram.com/reel/…\nhttps://www.tiktok.com/@…/video/…\nhttps://youtube.com/shorts/…'}
              className="min-h-[160px] font-mono text-xs"
            />
            <p className="text-xs text-muted-foreground">
              Можно вставить столбец из таблицы целиком — лишний текст отбросится, повторы уберутся.
            </p>
          </div>
          <Button onClick={handleCollect} disabled={isCollecting || !text.trim()} className="w-full">
            {isCollecting && <Loader2 className="h-4 w-4 animate-spin" />}
            Собрать
          </Button>
          {progress && <div className="p-3 bg-muted rounded-md text-sm text-muted-foreground">{progress}</div>}
        </CardContent>
      </Card>

      {rows.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="space-y-1.5">
                <CardTitle>Публикации ({rows.length})</CardTitle>
                <CardDescription>
                  {missing.length > 0
                    ? `Осталось вписать просмотры: ${missing.length}. Скрин можно вставить из буфера (Ctrl+V в строке) или выбрать файлом.`
                    : 'Всё заполнено. Word и Excel собираются из одних и тех же цифр.'}
                </CardDescription>
              </div>
              <ExportMenu
                rememberKey="report:docx-links"
                label="Выгрузить"
                formats={[FORMAT_GDOC, FORMAT_DOCX, FORMAT_XLSX]}
                onSelect={handleExport}
                busy={isExporting}
                busyLabel="Формируем отчёт…"
                className="shrink-0"
              />
            </div>
            {!isExporting && <GoogleDocsLinks urls={docUrls} />}
          </CardHeader>
          <CardContent className="space-y-3">
            {rows.map((row, i) => (
              <div
                key={`${row.url}-${i}`}
                onPaste={onPasteImage(i)}
                data-testid="minister-row"
                className={`grid gap-3 rounded-lg border p-3 md:grid-cols-[96px_1fr] ${
                  row.views == null ? 'border-amber-400 bg-amber-50/40 dark:bg-amber-950/10' : ''
                }`}
              >
                <label className="relative flex h-32 w-24 cursor-pointer items-center justify-center overflow-hidden rounded-md border bg-muted text-muted-foreground">
                  {row.imageUrl ? (
                    <img src={row.imageUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span className="flex flex-col items-center gap-1 text-[11px]">
                      <ImagePlus className="h-5 w-5" />
                      Скрин
                    </span>
                  )}
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => void attachImage(i, e.target.files?.[0])}
                  />
                </label>
                <div className="min-w-0 space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="text-sm text-muted-foreground tabular-nums">{i + 1}.</span>
                    <a href={row.url} target="_blank" rel="noreferrer" className="min-w-0 flex-1 truncate text-sm underline">
                      {row.url}
                    </a>
                    <Badge variant={row.source === 'auto' ? 'secondary' : 'outline'}>{KIND_LABEL[row.kind]}</Badge>
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => removeRow(i)} aria-label="Убрать">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                  {row.reason && <p className="text-xs text-amber-700 dark:text-amber-400">{row.reason}</p>}
                  <div className="grid gap-2 sm:grid-cols-[1fr_140px_150px]">
                    <Input
                      value={row.theme}
                      onChange={(e) => updateRow(i, { theme: e.target.value })}
                      placeholder="Тема"
                      aria-label="Тема"
                    />
                    <Input
                      inputMode="numeric"
                      value={row.views == null ? '' : String(row.views)}
                      onChange={(e) => {
                        const digits = e.target.value.replace(/\D/g, '');
                        updateRow(i, { views: digits === '' ? null : Number(digits) });
                      }}
                      placeholder="Просмотры"
                      aria-label="Просмотры"
                    />
                    <Input
                      type="date"
                      value={row.publishedAt ? format(new Date(row.publishedAt), 'yyyy-MM-dd') : ''}
                      onChange={(e) =>
                        updateRow(i, { publishedAt: e.target.value ? new Date(`${e.target.value}T12:00:00`).toISOString() : '' })
                      }
                      aria-label="Дата публикации"
                    />
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
